import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useExperienceEvents } from '@/features/events/hooks/useExperienceEvents';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useTodayIso } from '@/hooks/useTodayIso';
import type {
  ExperienceFeed,
  ListExperienceEventInput,
} from '@/types/gql.generated';

// How far ahead the source preview looks. Matches the legacy Schedule sources
// page (see pages/Schedule/.../useFeed.ts), which bounds its per-feed entry
// query the same way.
const PREVIEW_WINDOW_YEARS = 1;

/**
 * Events to preview for a single source, scoped server-side to today onward.
 *
 * The Sources panel deliberately does NOT reuse the experience-wide `allEvents`
 * from PageExperienceContext. That query is ordered oldest-first and capped at
 * 200 events server-side, so a calendar that imported months of history would
 * spend the whole budget on past events and the source's upcoming ones would
 * never be fetched at all — no client-side filter can recover them.
 *
 * Scoping with the `sources` facet keeps the cap per-source, and the server's
 * start bound is on `lastEndDate`, so multi-day and recurring events that began
 * in the past but are still running are retained.
 *
 * A recurring series comes back as one row still carrying its original start —
 * often years past, since that bound only asks that the series has not ended.
 * `expandSourceEventInstances` turns those into the occurrences the panel lists.
 *
 * Hidden events are included: this query doesn't filter on metadata.hidden
 * (only useVisibleExperienceEvents does), which is what lets the "show hidden"
 * toggle surface them.
 */
type Options = {
  /** False skips the request, for a source known to deliver nothing. */
  enabled?: boolean;
};

export const useSourceEvents = (
  feed: ExperienceFeed,
  { enabled = true }: Options = {}
) => {
  const { experience, displayTimezone } = usePageExperienceContext();

  // Tracks the display-zone calendar day and re-renders when it rolls over, so
  // a panel left open past midnight moves its window forward instead of staying
  // pinned to the day it mounted on.
  const todayIso = useTodayIso(displayTimezone);

  // The window the preview covers, also returned so the caller expands
  // recurrences over exactly the range the query was scoped to.
  const { rangeStart, rangeEnd } = useMemo(() => {
    const today = todayIso
      ? DateTime.fromISO(todayIso, { zone: displayTimezone })
      : DateTime.now().setZone(displayTimezone).startOf('day');
    return {
      rangeStart: today,
      rangeEnd: today.plus({ years: PREVIEW_WINDOW_YEARS }),
    };
  }, [displayTimezone, todayIso]);

  const filters = useMemo<ListExperienceEventInput>(
    () => ({
      sources: [feed.id],
      startDate: rangeStart.toISODate(),
      endDate: rangeEnd.toISODate(),
    }),
    [feed.id, rangeStart, rangeEnd]
  );

  const { events, isLoading, error } = useExperienceEvents({
    experience,
    filters,
    enabled,
  });

  return { events, isLoading, error, rangeStart, rangeEnd };
};
